export interface Material {
  id: string;
  name: string;
  family: "Wood" | "Resin" | "Natural" | "Specialty" | "Metal";
  appearance: string;
  variation: string;
  suits: string[];
  example: string;
  swatch: string;
}

export const materials: Material[] = [
  {
    id: "walnut",
    name: "Black Walnut",
    family: "Wood",
    appearance: "Deep chocolate brown with occasional purple and gold streaks.",
    variation: "Color lightens toward the sapwood; every board differs.",
    suits: ["Pens", "Bottle stoppers", "Signs", "Carvings"],
    example: "Bolt-Action Pen in Black Walnut",
    swatch: "oklch(0.36 0.055 55)",
  },
  {
    id: "maple-burl",
    name: "Maple Burl",
    family: "Wood",
    appearance: "Pale honey ground covered in tight swirling eyes.",
    variation: "No two blanks share a pattern; voids are common and stabilized.",
    suits: ["Fountain pens", "Rollerballs", "Keepsake boxes"],
    example: "Majestic Fountain Pen in Maple Burl",
    swatch: "oklch(0.79 0.07 78)",
  },
  {
    id: "cherry",
    name: "Cherry",
    family: "Wood",
    appearance: "Warm reddish brown with a fine, even grain.",
    variation: "Darkens noticeably with light and age.",
    suits: ["Pen and pencil sets", "Engraved boards", "Carvings"],
    example: "Pen and Pencil Set in Cherry",
    swatch: "oklch(0.55 0.08 46)",
  },
  {
    id: "osage",
    name: "Osage Orange",
    family: "Wood",
    appearance: "Bright golden yellow that ambers over time.",
    variation: "Dense and oily; the color shift is part of the character.",
    suits: ["Duck calls", "Pens", "Turned gifts"],
    example: "Duck Call in Osage Orange",
    swatch: "oklch(0.72 0.13 85)",
  },
  {
    id: "resin",
    name: "Cast Acrylic Resin",
    family: "Resin",
    appearance: "Saturated color, swirls, pearl and translucency.",
    variation: "Poured in batches, so exact patterns cannot be repeated.",
    suits: ["Cigar-style pens", "Fidget spinners", "Bottle stoppers"],
    example: "Cigar-Style Pen in Cast Resin",
    swatch: "oklch(0.55 0.14 235)",
  },
  {
    id: "clear-cast",
    name: "Clear Cast with Embedded Objects",
    family: "Specialty",
    appearance: "Glass-clear resin holding a keepsake in suspension.",
    variation: "Depends entirely on the object supplied.",
    suits: ["Memorial pens", "Blanks for turners"],
    example: "Memorial Pen with Embedded Keepsake",
    swatch: "oklch(0.86 0.02 220)",
  },
  {
    id: "antler",
    name: "Antler",
    family: "Natural",
    appearance: "Cream to bone white with a porous marbled core.",
    variation: "Naturally shed and irregular; each barrel is unique.",
    suits: ["Designer pens", "Duck calls", "Bottle stoppers"],
    example: "Designer Pen in Antler",
    swatch: "oklch(0.88 0.02 85)",
  },
  {
    id: "fordite",
    name: "Fordite",
    family: "Specialty",
    appearance: "Hundreds of layers of baked automotive paint in bright bands.",
    variation: "Cut from reclaimed material, so runs are finite.",
    suits: ["Pens", "Blanks for turners"],
    example: "Fordite Pen Blanks",
    swatch: "oklch(0.6 0.16 20)",
  },
  {
    id: "brass",
    name: "Gold, Chrome and Gunmetal Fittings",
    family: "Metal",
    appearance: "Trim, clips and nibs that set the tone of the finished piece.",
    variation: "Availability depends on the pen kit chosen.",
    suits: ["All pens", "Bottle stoppers"],
    example: "Majestic Fountain Pen in Maple Burl",
    swatch: "oklch(0.652 0.093 76)",
  },
];

export interface FaqItem {
  q: string;
  a: string;
  pending?: boolean;
}

export const faqs: FaqItem[] = [
  {
    q: "Can Paul create something that is not pictured?",
    a: "Yes. Most of what Paul makes starts as a conversation rather than a catalog item. If you can describe the piece, who it is for and roughly what you are imagining, Paul will tell you what is realistic and suggest materials that suit it.",
  },
  {
    q: "How does pricing work?",
    a: "Pricing depends on the materials, the hardware and the amount of work a piece needs. Paul reviews your request and gives you a price before anything is made. There is no obligation at that point.",
  },
  {
    q: "How do I place a custom order?",
    a: "Use the Design Your Piece form to describe what you have in mind, or email or call Paul directly. He replies to discuss the details, confirm the design and provide pricing.",
  },
  {
    q: "How does payment work?",
    a: "Once the details are agreed, Paul sends a PayPal invoice. Payment is handled through PayPal's secure checkout — Paul never handles your card details.",
  },
  {
    q: "How long does a custom order take?",
    a: "Timing depends on the piece, the materials and what is already on the bench. Paul confirms a realistic timeframe with you before you commit.",
    pending: true,
  },
  {
    q: "Can items be shipped?",
    a: "Shipping arrangements are confirmed with Paul when your order is discussed, including cost and any local pickup options.",
    pending: true,
  },
  {
    q: "Can I supply my own material or keepsake?",
    a: "Often, yes. Wood from a family property, a coin, a cartridge case or a small keepsake can frequently be worked into a piece. Send Paul a photo and he will tell you whether it can be turned or cast safely.",
  },
  {
    q: "Can Paul carve a company or team logo?",
    a: "Only if you own the artwork or have written permission to use it. Paul does not reproduce trademarked or copyrighted logos without that permission.",
  },
  {
    q: "Do you sell to other pen turners?",
    a: "Yes. Paul keeps blanks, cartridge components and clear-cast material for other turners. See the For Pen Turners page and ask about current availability.",
  },
  {
    q: "Where can I see the work in person?",
    a: "Paul sells at the ASU Farmers Market on Saturday mornings from May through October and at craft shows during the year. Contact Paul to confirm the current schedule before travelling.",
    pending: true,
  },
];

export const processSteps = [
  {
    n: "01",
    title: "Share your idea",
    body: "Tell Paul what you want, who it is for and what makes it meaningful. Photos and rough sketches help.",
  },
  {
    n: "02",
    title: "Confirm the details",
    body: "Paul reviews the materials, design, timing and estimated cost with you, and suggests options if something is not workable.",
  },
  {
    n: "03",
    title: "Receive your invoice",
    body: "Once the project is confirmed, Paul sends a PayPal invoice. Nothing is charged before you agree to the details.",
  },
  {
    n: "04",
    title: "Paul creates your piece",
    body: "The item is handcrafted to the approved details, inspected, and prepared for the person receiving it.",
  },
];

export const craftSteps = [
  {
    title: "Selecting the material",
    body: "The blank is chosen for figure, density and how it will behave once it is round. Some blanks are set aside for months waiting for the right project.",
  },
  {
    title: "Shaping and turning",
    body: "The piece is cut oversize and brought down slowly. This is where the profile, the balance and the way it sits in the hand are decided.",
  },
  {
    title: "Sanding and finishing",
    body: "Progressive grits, then finish applied in thin coats. This stage takes longer than the turning and is the difference you feel later.",
  },
  {
    title: "Fitting the hardware",
    body: "Components are pressed and aligned so the cap seats cleanly and the mechanism works the way it should for years.",
  },
  {
    title: "Inspecting the details",
    body: "Every seam, transition and edge is checked under good light. Anything that does not read as finished goes back to the bench.",
  },
  {
    title: "Preparing the piece",
    body: "The finished item is cleaned, packed and made ready for the person who is going to open it.",
  },
];

export const giftFinder = {
  recipients: [
    "Partner",
    "Parent",
    "Friend",
    "Teacher",
    "Graduate",
    "Veteran",
    "Coworker",
    "Executive",
    "Someone else",
  ],
  occasions: [
    "Birthday",
    "Graduation",
    "Retirement",
    "Anniversary",
    "Holiday",
    "Recognition",
    "Memorial",
    "Just because",
  ],
  styles: ["Traditional", "Modern", "Rustic", "Bold", "Elegant", "Outdoors-inspired", "Not sure"],
  budgets: ["Modest", "Mid-range", "Special occasion", "I would like Paul's guidance"],
};

export const wizardOptions = {
  interest: [
    "Custom pen",
    "Pen-and-pencil set",
    "Bottle stopper",
    "Duck call",
    "CNC or engraved item",
    "Sign",
    "Cornhole board",
    "Pen-turning component",
    "Something else",
  ],
  recipient: ["Myself", "A gift recipient", "A business or organization", "Not sure yet"],
  occasions: [
    "Birthday",
    "Graduation",
    "Retirement",
    "Anniversary",
    "Holiday",
    "Recognition",
    "Memorial",
    "Just because",
    "Other",
  ],
  styles: [
    "Traditional",
    "Modern",
    "Rustic",
    "Bold",
    "Elegant",
    "Outdoors-inspired",
    "Military-inspired",
    "Faith-inspired",
    "Not sure",
  ],
  materials: [
    "Natural wood",
    "Burl or figured wood",
    "Acrylic or resin",
    "Antler",
    "Fordite or colorwood",
    "Cartridge components",
    "Embedded keepsake",
    "I would like Paul's recommendation",
  ],
  personalization: [
    "A name",
    "A date",
    "A short message",
    "Engraving",
    "A custom image or concept",
    "No personalization",
    "Not sure",
  ],
  // Configurable rather than hardcoded in the component
  budgets: ["Under $75", "$75 – $150", "$150 – $300", "$300 – $600", "$600+", "Not sure yet"],
  contactMethods: ["Email", "Telephone", "Either is fine"],
};

export const trustPoints = [
  "Custom-made",
  "Crafted in Arkansas",
  "Secure PayPal invoicing",
  "Making pens since 2006",
];
